import React, { useState, useEffect } from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  useTranslate,
  useNotify,
  useEditController,
  useRedirect,
  required,
} from 'react-admin'
import { TransferList } from 'src/components/uiElements/react-hook-fields/TransferList'
import { Grid, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { applicationServices } from 'src/services/auth/application/application.service'
import { clientServices } from 'src/services/auth/client/client.service'
import { permissionServices } from 'src/services/auth/permission/permission.service'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'

export const RoleEdit = ({ id, ...props }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [clients, setClients] = useState([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [applications, setApplications] = useState([])
  const [loadingApplications, setLoadingApplications] = useState(false)
  const [unselectedPermissions, setUnselectedPermissions] = useState([])
  const [selectedPermissions, setSelectedPermissions] = useState([])
  const [loadingPermissions, setLoadingPermissions] = useState(false)
  const { record, isFetching } = useEditController({ resource: 'role', id })

  useEffect(() => {
    if (record && !isFetching) {
      getApplicationByClient(record.client_id)
      getPermissionsByApplication(record.application_id, record.permission_by_role)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, isFetching])

  useEffect(() => {
    ;(async () => {
      setLoadingClients(true)
      try {
        const allClients = await clientServices.getByAll()
        if (allClients) {
          setClients(allClients)
          setLoadingClients(false)
          return
        }
        notify(`applications not found`, { type: 'error' })
        setLoadingClients(false)
      } catch (error) {
        notify(`please, refresh page`, { type: 'error' })
        setLoadingClients(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getApplicationByClient = async (idClient) => {
    if (idClient) {
      try {
        setLoadingApplications(true)
        let allApplications = await applicationServices.getApplicationByClient(idClient)
        setApplications(allApplications.map((item) => item.application))
        setLoadingApplications(false)
      } catch (error) {
        notify(`applications not found`, { type: 'error' })
        setLoadingApplications(false)
        return
      }
    }
  }

  const mapperPermission = (data) => {
    return data.map((perm) => ({
      ...perm,
      ...perm.permission,
    }))
  }

  const permissionWithoutSelect = (selected, options) => {
    const optionPermission = []
    if (selected.length > 1) {
      const id_selected = selected.map((item) => item.id)
      options.forEach((perm) => {
        if (!id_selected.includes(perm.id)) {
          optionPermission.push(perm)
        }
      })
      return optionPermission
    }
    return options
  }

  const getPermissionName = (permission) => {
    let text = [
      permission.id,
      permission.description
    ]

    if (permission?.entity_relation?.name) {
      text.push(translate(`ra.entity.${permission?.entity_relation?.name}`))
    }

    if (permission?.action) {
      text.push(translate(`ra.action.${permission?.action}`))
    }

    return text.join(' - ')
  }

  const getPermissionsByApplication = async (idApplication, permissionByRol) => {
    if (idApplication) {
      try {
        setLoadingPermissions(true)
        const allPermissions = await permissionServices.getPermissionsByApplications(idApplication)
        if (allPermissions) {
          const selected = mapperPermission(permissionByRol)
          const optionsWithoutSelect = permissionWithoutSelect(selected, allPermissions)
          setUnselectedPermissions(optionsWithoutSelect.map(permission => ({
            ...permission, 
            showField: getPermissionName(permission)
          })))
          setSelectedPermissions(selected.map(permission => ({
            ...permission, 
            showField: getPermissionName(permission)
          })))
          setLoadingPermissions(false)
          return
        }
        setUnselectedPermissions([])
        setSelectedPermissions([])
        setLoadingPermissions(false)
      } catch (error) {
        setUnselectedPermissions([])
        setSelectedPermissions([])
        notify(`permissions not found`, { type: 'error' })
        setLoadingPermissions(false)
        return
      }
    }
  }

  const transform = (data) => ({
    id: data.id,
    client_id: data.client_id,
    application_id: data.application_id,
    short_name: data.short_name,
    long_name: data.long_name,
    permission_id: data.permission_id ? data.permission_id.map((permission) => permission.id) : [],
  })

  const onSuccess = () => {
    notify('ra.notification.updated_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.role') },
    })
    redirect('/role')
  }

  const onError = () => {
    notify('ra.notification.updated_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.role') },
    })
  }

  return (
    <Edit
      {...props}
      transform={transform}
      mutationMode='pessimistic'
      mutationOptions={{ onSuccess, onError }}
      actions={null}
    >
      <CardCommon title={translate('ra.title.update_role')}>
        <SimpleForm
          toolbar={
            <CardCrudBottomButtons
              btnSave={true}
              btnDelete={true}
              fieldsName={['short_name']}
              genderElement='masculine'
            />
          }
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4} className='uiInputLabel'>
              <EditInputsWrap label={translate('ra.field.id')} md={12}>
                <TextInput disabled source='id' label='' fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.short_name')} md={12} required>
                <TextInput source='short_name' label='' fullWidth validate={[required()]} />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.long_name')} md={12}>
                <TextInput source='long_name' label='' fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.client')} md={12} required>
                {loadingClients ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    source='client_id'
                    optionsText={['long_name']}
                    label=''
                    fullWidth
                    options={clients}
                    defaultValue={record?.client_id}
                    disabled
                  />
                )}
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.application')} md={12} required>
                {loadingApplications ? (
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                ) : (
                  <SelectInput
                    source='application_id'
                    optionsText={['long_name']}
                    options={applications}
                    defaultValue={record?.application_id}
                    label=''
                    fullWidth
                    disabled
                  />
                )}
              </EditInputsWrap>
            </Grid>
            <Grid item xs={12} sm={8} className='uiInputLabel'>
              <EditInputsWrap
                // tooltiptext={translate('ra.message.permission_tooltip')}
                label={translate('ra.field.permission')}
                md={12}
              >
                {loadingPermissions ? (
                  <Grid
                    container
                    spacing={2}
                    justifyContent='center'
                    alignItems='center'
                    className='transferListComponent'
                  >
                    <Grid item xs={5}>
                      <Skeleton variant='rectangular' width={'100%'} height={500} />
                    </Grid>
                    <Grid item xs={2}>
                      <Grid container direction='column' alignItems='center'>
                        <Skeleton variant='rectangular' width={'100%'} height={40} />
                      </Grid>
                    </Grid>
                    <Grid item xs={5}>
                      <Skeleton variant='rectangular' width={'100%'} height={500} />
                    </Grid>
                  </Grid>
                ) : (
                  <TransferList
                    source='permission_id'
                    options={unselectedPermissions}
                    selected={selectedPermissions}
                    labelField='showField'
                  />
                )}
              </EditInputsWrap>
            </Grid>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}
